<template>
  <Layout>
    <Loader :loading="loading" no-axios>
      <div v-if="(model || {}).id">
        <div class="row">
          <div class="col-12">
            <a @click="$router.back()">
              <div class="container-title d-flex align-items-center">
                <span class="me-2">
                  <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M6.875 15.3001L0.275 8.70008C0.175 8.60008 0.104 8.49175 0.0619998 8.37508C0.0206665 8.25842 0 8.13342 0 8.00008C0 7.86675 0.0206665 7.74175 0.0619998 7.62508C0.104 7.50842 0.175 7.40008 0.275 7.30008L6.875 0.700082C7.05833 0.516749 7.28733 0.420749 7.562 0.412083C7.83733 0.404083 8.075 0.500082 8.275 0.700082C8.475 0.883416 8.57933 1.11242 8.588 1.38708C8.596 1.66242 8.5 1.90008 8.3 2.10008L3.4 7.00008H14.575C14.8583 7.00008 15.096 7.09575 15.288 7.28708C15.4793 7.47908 15.575 7.71675 15.575 8.00008C15.575 8.28342 15.4793 8.52075 15.288 8.71208C15.096 8.90408 14.8583 9.00008 14.575 9.00008H3.4L8.3 13.9001C8.48333 14.0834 8.57933 14.3167 8.588 14.6001C8.596 14.8834 8.5 15.1167 8.3 15.3001C8.11667 15.5001 7.88333 15.6001 7.6 15.6001C7.31667 15.6001 7.075 15.5001 6.875 15.3001Z"
                        fill="#6C7589"/>
                  </svg>
                </span>
                {{ $t("{something} Details", {
                  something: $t("purchase_plans.singular")
                }) }}
              </div>
            </a>
          </div>
          <div class="col-md-12 col-lg-8">
            <b-card v-bind="cardAttrs">
              <div class="row">
                <div class="col-md-6 d-flex align-items-center">
                  <h5 class="approval-history-header mb-0">
                    {{ lodash.startCase($t('purchase_plans.general_information')) }}
                  </h5>
                </div>
                <div class="col-md-6 d-flex justify-content-end gap-2">
                  <template v-if="isEditing">
                    <b-button
                        :disabled="saving"
                        variant="danger"
                        @click="cancelEditing"
                    >
                      {{ lodash.startCase($t('purchase_plans.buttons.cancel_changes')) }}
                    </b-button>
                    <b-button
                        :disabled="saving || !hasChanges"
                        variant="success"
                        @click="submitChanges"
                    >
                      <b-spinner v-if="saving" class="me-2" small></b-spinner>
                      {{ lodash.startCase($t('purchase_plans.buttons.save_changes')) }}
                    </b-button>
                  </template>
                  <template v-else>
                    <b-button
                        v-if="showDuplicateButton"
                        variant="outline-primary"
                        @click="handleDuplicate"
                    >
                      <i class="bx bx-copy-alt font-size-16 align-middle"></i>
                      {{ lodash.startCase($t('purchase_plans.buttons.duplicate')) }}
                    </b-button>
                    <b-button
                        v-if="showEditButton"
                        class="btn-edit-amber"
                        variant="outline-primary"
                        :to="`/purchase_plans/${model.id}/edit`"
                    >
                      <i class="bx bx-edit font-size-16 align-middle"></i>
                      {{ $t('Edit') }}
                    </b-button>
                    <b-button
                        v-else-if="showInlineEditButton"
                        class="btn-edit-amber"
                        variant="outline-primary"
                        @click="startEditing"
                    >
                      <i class="bx bx-edit font-size-16 align-middle"></i>
                      {{ $t('Edit') }}
                    </b-button>
                    <b-button
                        v-if="showDeleteButton"
                        variant="outline-danger"
                        @click="handleDelete"
                    >
                      <i class="bx bx-trash font-size-16 align-middle"></i>
                      {{ lodash.startCase($t('purchase_plans.buttons.delete')) }}
                    </b-button>
                    <b-button
                        v-if="showCancelButton"
                        variant="outline-danger"
                        @click="handleCancel"
                    >
                      <i class="bx bx-x font-size-16 align-middle"></i>
                      {{ lodash.startCase($t('purchase_plans.buttons.cancel')) }}
                    </b-button>
                    <b-button
                        v-if="showRejectButton"
                        :disabled="rejecting"
                        variant="danger"
                        @click="handleReject"
                    >
                      <b-spinner v-if="rejecting" class="me-2" small></b-spinner>
                      {{ lodash.startCase($t('purchase_plans.buttons.reject')) }}
                    </b-button>
                    <b-button
                        v-if="showApproveButton"
                        :disabled="approving"
                        variant="success"
                        @click="handleApprove"
                    >
                      <b-spinner v-if="approving" class="me-2" small></b-spinner>
                      {{ lodash.startCase($t('purchase_plans.buttons.approve')) }}
                    </b-button>
                  </template>
                </div>
              </div>
              <hr>
              <div class="row">
                <div class="col-md-6 d-flex flex-column gap-3">
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.upperFirst($t("purchase_plans.sales_plan_id")) }}
                    </div>
                    <div class="text-neutral-600">
                      <router-link v-if="model.sales_plan" :to="`/sales_plans/${model.sales_plan.id}`">
                        {{ salesPlanDisplayName(model.sales_plan) }}
                      </router-link>
                      <span v-else>-</span>
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.upperFirst($t("purchase_plans.product")) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ (model.product || {}).name || model.product_name || "-" }}
                      {{ model.payment_type === 'down_payment_80' ? ' 80%' : '' }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.upperFirst($t("purchase_plans.partner")) }}
                    </div>
                    <div class="text-neutral-600">
                      <router-link v-if="(model.partner || {}).id" :to="`/customers/${model.partner.id}`">
                        {{ model.partner.name }}
                      </router-link>
                      <span v-else>{{ model.partner_name || '-' }}</span>
                    </div>
                  </div>
                  <!-- creator -->
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t("CreatedBy")) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ (model.creator || {}).name  || '-' }}
                    </div>
                  </div>
                </div>
                <div class="col-md-6 d-flex flex-column gap-3">
                  <div class="description-detail">
                    <div :class="{ required: isEditingSubmittedAt }" class="label font-size-500">
                      {{ lodash.upperFirst($t("purchase_plans.submitted_at")) }}
                    </div>
                    <div class="text-neutral-600">
                      <date-picker
                          v-if="isEditingSubmittedAt"
                          v-model="editingSubmittedAt"
                          :clearable="false"
                          :disabled="saving"
                          format="DD MMM YYYY"
                          name="submitted_at"
                          type="date"
                      />
                      <template v-else>
                        {{ model.submitted_at ? $moment(model.submitted_at).format('DD MMM YYYY') : "-" }}
                      </template>
                    </div>
                  </div>
                  <div class="description-detail">
                    <div :class="{ required: isEditingProcessing }" class="label font-size-500">
                      {{ lodash.upperFirst($t("purchase_plans.processing")) }}
                    </div>
                    <div class="text-neutral-600">
                      <customer-select-ajax
                          v-if="isEditingProcessing"
                          v-model.number="editingProcessingId"
                          name="processing_id"
                          :params="{ active: 1, type__like_in: 'processing' }"
                          :disabled="saving"
                      />
                      <template v-else>
                        <router-link v-if="(model.processing || {}).id" :to="`/customers/${model.processing.id}`">
                          {{ model.processing.name }}
                        </router-link>
                        <span v-else>{{ model.processing_name || '-' }}</span>
                      </template>
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.upperFirst($t("purchase_plans.supervisor")) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ (model.supervisor || {}).name || "-" }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t("purchase_plans.status")) }}
                    </div>
                    <div class="text-neutral-600">
                      <status-badge
                          v-if="model.status"
                          :status="model.status"
                          entity="purchase_plan"
                          type="status"
                      ></status-badge>
                      <span v-else>-</span>
                    </div>
                  </div>
                </div>
                <div class="col-12 mt-4">
                  <div class="table-container card">
                    <b-table-simple borderless class="mb-0">
                      <b-thead>
                        <b-tr>
                          <b-th>{{ lodash.startCase($t('purchase_plans.limit')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('purchase_plans.estimated_amount')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('purchase_plans.estimated_size')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('purchase_plans.estimated_quantity')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('purchase_plans.estimated_margin')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('purchase_plans.realized_amount')) }}</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr>
                          <b-td>
                            <b-badge v-if="limitBadge" :variant="limitBadge.variant">
                              {{ limitBadge.label }}
                            </b-badge>
                            <span v-else>-</span>
                          </b-td>
                          <b-td>{{ currencyFormatter(model.estimated_amount) }}</b-td>
                          <b-td>{{
                              model.estimated_size_start !== null && model.estimated_size_end !== null
                              ? `${decimalFormatter(model.estimated_size_start)} - ${decimalFormatter(model.estimated_size_end)}`
                              : "-"
                            }}
                          </b-td>
                          <b-td>{{ decimalFormatter(model.estimated_quantity) }} {{ $t("units.kg") }}</b-td>
                          <b-td>{{ currencyFormatter(model.estimated_margin) }}</b-td>
                          <b-td>{{ model.realized_amount ? `${currencyFormatter(model.realized_amount)}` : "-" }}</b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </div>
                </div>
                <div class="col-12" v-if="model.processing_id">
                  <b-card class="border">
                    <h5 class="approval-history-header mb-0">
                      {{ lodash.startCase($t('purchase_plans.processing_data')) }}
                    </h5>
                    <div class="row mt-3">
                      <div class="col">
                        <div class="d-flex justify-content-between">
                          <div>
                            [{{ extractProcessingCode(model.processing) }}] {{ extractProcessingName(model.processing) }}
                          </div>
                          <div class="d-flex gap-3">
                            <div style="font-weight: 700;">{{ lodash.startCase($t('purchase_plans.dpd_status')) }}</div>
                            <b-badge
                                :variant="daysPastDueVariant(processingMaxDaysPastDueIn90Days)"
                                class="d-block"
                            >
                              {{ formattedDaysPastDue(processingMaxDaysPastDueIn90Days) }}
                            </b-badge>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="pbarik-progress-section">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div class="processing-limit-progress-meta">
                            {{ lodash.upperFirst($t('usage')) }}:
                            <b style="font-weight: 700; color: #1B91FF">{{
                                currencyFormatter(processingUsedAmount)
                              }}</b>
                          </div>
                        </div>
                        <div class="processing-limit-progress-meta text-end">
                          {{ lodash.upperFirst($t('remaining')) }}:
                          <b style="font-weight: 700; color: #22C55E">{{
                              currencyFormatter(processingAvailableAmount)
                            }}</b>
                        </div>
                      </div>
                      <b-progress
                          :max="100"
                          :value="processingUsedAmountProgressPercentage"
                          height="10px"
                          variant="primary"
                      ></b-progress>
                      <div class="d-flex justify-content-end align-items-start mb-2">
                        <div class="processing-limit-progress-meta">
                          {{
                            `${lodash.upperFirst($t('limit'))}: ${currencyFormatter(processingLimitAmount)}`
                          }}
                        </div>
                      </div>
                    </div>
                  </b-card>
                </div>
              </div>
              <b-card-text class="change-history-card-text">
                <div class="change-history-title">{{ lodash.startCase($t(`${resourceRoute}.change_history`)) }}</div>
                <div class="mb-1">
                  <ul id="activity-log-list" class="mb-0" style="margin-left: -12px">
                    <li
                        v-for="(formattedActivityLog, index) in formattedActivityLogsToShow"
                        :key="`activity-logs-${index}`"
                        class="mt-2"
                        v-html="formattedActivityLog"
                    >
                    </li>
                  </ul>
                </div>
                <template v-if="formattedActivityLogs.length > maxActivityToShowOnCollapse">
                  <b-link
                      class="activity-log-collapse"
                      href="#"
                      @click.prevent="openActivityLogModal"
                  >
                    {{ lodash.startCase($t('See All')) }}
                  </b-link>
                </template>
              </b-card-text>
            </b-card>
          </div>
          <div class="col-md-12 col-lg-4">
            <b-card>
              <div class="col-12">
                <h5 class="approval-history-header mb-0">
                  {{ lodash.startCase($t('purchase_plans.approval_history')) }}
                </h5>
              </div>
              <hr>
              <template v-for="(step, index) in approvalProgressSteps">
                <approval-progress-step
                    :key="approvalStepKey(step, index)"
                    :is-last="index === approvalProgressSteps.length - 1"
                    :step="step"
                />
              </template>
            </b-card>
            <b-card v-if="submittedBalancingSaleOrders.length > 0">
              <div class="col-12">
                <h5 class="approval-history-header mb-0">
                  {{ lodash.startCase($t('balancing_approvals.history_title')) }}
                </h5>
              </div>
              <hr>
              <div v-if="balancingSwitcherTabs.length > 1" class="balancing-switcher d-flex flex-nowrap mb-3">
                <button
                    v-for="tab in balancingSwitcherTabs"
                    :key="tab.name"
                    type="button"
                    class="balancing-switcher-tab"
                    :class="{ active: tab.name === activeBalancingSaleOrderId }"
                    @click="activeBalancingSaleOrderId = tab.name"
                >
                  <span class="balancing-switcher-dot" :style="{ backgroundColor: tab.dotColor }"></span>
                  {{ tab.label }}
                </button>
              </div>
              <balancing-approval-history
                  v-if="activeBalancingSaleOrder"
                  :key="`balancing-approval-history-${activeBalancingSaleOrder.id}`"
                  :sale-order="activeBalancingSaleOrder"
                  :initial-open-cycle-id="targetBalancingApprovalId"
              />
            </b-card>
          </div>
          <div v-if="['approved', 'cancel'].indexOf(model.status) > -1" class="col-md-12">
            <b-card>
              <purchase-plan-relation-tabs v-model="activeRelationTab" :modes="relationTabModes" class="mb-3" />
              <purchase-orders v-if="activeRelationTab === 'purchase_orders'" :parent="model" />
              <trips v-else-if="activeRelationTab === 'trips'" :parent="model" />
              <unloads v-else-if="activeRelationTab === 'unloads'" :parent="model" parent-filter-key="where_has_purchase_plan_id__in" />
              <sale-notes v-else-if="activeRelationTab === 'sale_notes'" :parent="model" parent-filter-key="where_has_purchase_plan_id__in" />
              <balancing v-else-if="activeRelationTab === 'balancing'" :parent="model" />
            </b-card>
          </div>
        </div>

        <purchase-plan-delete-confirmation
            ref="deleteConfirmation"
            @deleted="onDeleted"
        />
        <purchase-plan-cancel-confirmation
            ref="cancelConfirmation"
            @cancelled="onCancelled"
        />
        <purchase-plan-duplicate-confirmation
            ref="duplicateConfirmation"
        />
        <purchase-plan-submitted-at-confirmation
            ref="submittedAtConfirmation"
        />

        <b-modal
            v-model="activityLogModalShow"
            :title="lodash.startCase($t(`${resourceRoute}.change_history`))"
            centered
            hide-footer
            scrollable
            size="lg"
            title-class="activity-log-modal-title"
        >
          <div class="activity-log-modal-content">
            <ul id="activity-log-modal-list" class="mb-0 ps-3">
              <li
                  v-for="(formattedActivityLog, index) in formattedActivityLogs"
                  :key="`activity-log-modal-${index}`"
                  class="mt-2"
                  v-html="formattedActivityLog"
              >
              </li>
            </ul>
          </div>
        </b-modal>
        <purchase-plan-approve-confirmation
            ref="purchase-plan-approve-confirmation"
        />
        <purchase-plan-reject-confirmation
            ref="purchase-plan-reject-confirmation"
        />
        <purchase-plan-action-conflict-dialog
            ref="purchase-plan-action-conflict-dialog"
        />
      </div>
      <purchase-plan-load-error-dialog
          ref="purchasePlanLoadErrorDialog"
      />
    </Loader>
  </Layout>
</template>

<script>
import ModelShow from "../models/show";
import Loader from '@/components/widgets/loader';
import { currencyFormatter, decimalFormatter } from "@/helpers/ui/formatters";
import { daysPastDueVariant } from "@/helpers/ui/badgeVariants";
import PurchasePlanApproveConfirmation from '@/components/modals/purchase-plan-approve-confirmation.vue'
import PurchasePlanRejectConfirmation from '@/components/modals/purchase-plan-reject-confirmation.vue'
import PurchasePlanActionConflictDialog from '@/components/modals/purchase-plan-action-conflict-dialog.vue'
import PurchasePlanDuplicateConfirmation from '@/components/modals/purchase-plan-duplicate-confirmation.vue'
import PurchasePlanCancelConfirmation from '@/components/modals/purchase-plan-cancel-confirmation.vue'
import PurchasePlanDeleteConfirmation from '@/components/modals/purchase-plan-delete-confirmation.vue'
import PurchasePlanSubmittedAtConfirmation from '@/components/modals/purchase-plan-submitted-at-confirmation.vue'
import ApprovalProgressStep from '@/components/purchase-plans/approval-progress-step.vue'
import {collapseApprovalProgressSteps} from '@/utils/balancing_approval/approvalProgress'
import BalancingApprovalHistory from '@/components/purchase-plans/balancing-approval-history.vue'
import { serialize } from 'object-to-formdata'
import Swal from 'sweetalert2'
import CustomerSelectAjax from "@/components/inputs/customer-select-ajax.vue";
import DatePicker from "@/components/inputs/datepicker";
import PurchaseOrders from "./purchase_orders/index.vue";
import Trips from "./trips/index.vue";
import Unloads from "../sales_plans/unloads/index.vue";
import SaleNotes from "../sales_plans/sale_notes/index.vue";
import PurchasePlanRelationTabs from "./show/relation-tabs.vue";
import Balancing from "./balancing/index.vue";
import purchasePlanLoadError from "@/components/mixins/purchase-plan-load-error";
import { salesPlanDisplayName } from "@/helpers/ui/salesPlanLabels";

export default {
  extends: ModelShow,
  mixins: [purchasePlanLoadError],
  page: {
    title: "Detail Purchase Plan",
  },
  components: {
    Loader,
    PurchasePlanApproveConfirmation,
    PurchasePlanRejectConfirmation,
    PurchasePlanActionConflictDialog,
    PurchasePlanDuplicateConfirmation,
    PurchasePlanCancelConfirmation,
    PurchasePlanDeleteConfirmation,
    PurchasePlanSubmittedAtConfirmation,
    ApprovalProgressStep,
    BalancingApprovalHistory,
    CustomerSelectAjax,
    DatePicker,
    PurchaseOrders,
    Trips,
    Unloads,
    SaleNotes,
    PurchasePlanRelationTabs,
    Balancing,
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("purchase_plans.singular"));
      },
    },
    resourceRoute: { default: "purchase_plans" },
    modelVariable: { default: "purchase_plan" },
    denyEdit: { type: Boolean, default() { return !this.$auth.hasPermissionTo("purchase_plans.edit") } },
    denyDelete: { type: Boolean, default: true },
  },
  data() {
    return {
      title: this.lodash.upperFirst(this.$t("purchase_plans.plural")),
      maxActivityToShowOnCollapse: 5,
      activity_logs: [],
      activityLogModalShow: false,
      approving: false,
      rejecting: false,
      isEditing: false,
      saving: false,
      editingProcessingId: null,
      editingSubmittedAt: null,
      activeRelationTab: this.$route.query.balancing_approval_id ? "balancing" : "purchase_orders",
      processingCustomer: null,
      balancingSaleOrders: [],
      activeBalancingSaleOrderId: null,
      targetBalancingApprovalId: this.$route.query.balancing_approval_id ? String(this.$route.query.balancing_approval_id) : null,
    };
  },
  mounted() {
    this.fetchData();
  },
  computed: {
    isHybridProduct() {
      return ((this.model.product || {}).code || '').toLowerCase() === 'hybrid';
    },
    submittedBalancingSaleOrders() {
      return this.balancingSaleOrders.filter((saleOrder) => {
        return Array.isArray(saleOrder.balancing_approval_cycles) && saleOrder.balancing_approval_cycles.length > 0;
      });
    },
    balancingSwitcherTabs() {
      return this.submittedBalancingSaleOrders.map((saleOrder) => {
        const transactionId = saleOrder?.balancing_transaction?.id;

        return {
          name: `${saleOrder.id}`,
          label: transactionId ? `ID: ${transactionId}` : `${saleOrder.type_name}-${saleOrder.id}`,
          dotColor: this.balancingStatusDotColor(saleOrder.balancing_approval_status),
        };
      });
    },
    activeBalancingSaleOrder() {
      return this.submittedBalancingSaleOrders.find((saleOrder) => `${saleOrder.id}` === this.activeBalancingSaleOrderId)
          ?? this.submittedBalancingSaleOrders[0]
          ?? null;
    },
    relationTabModes() {
      const modes = [
        { name: 'purchase_orders', label: this.lodash.startCase(this.$t('purchase_orders.plural')) },
      ];

      modes.push({ name: 'trips', label: this.lodash.startCase(this.$t('trips.plural')) });

      if (this.model.sales_plan) {
        modes.push({ name: 'unloads', label: this.lodash.startCase(this.$t('unloads.plural')) });
      }

      if (this.model.sales_plan || this.isHybridProduct) {
        modes.push({ name: 'sale_notes', label: this.lodash.startCase(this.$t('sale_notes.plural')) });
      }

      if (this.balancingSaleOrders.length > 0 && !this.isHybridProduct) {
        modes.push({ name: 'balancing', label: this.lodash.startCase(this.$t('sale_notes.resumes.balancing')) });
      }

      return modes;
    },
    items() {
      return [
        {
          text: this.lodash.upperFirst(this.$t("purchase_plans.plural")),
          href: "/purchase_plans",
        },
        {
          text: this.model && this.model.id ? `#${this.model.id}` : this.$route.params.key,
          href: `/purchase_plans/${this.$route.params.key}`,
          active: true,
        },
      ];
    },
    processingMaxDaysPastDueIn90Days() {
      return this.model?.processing?.odoo_res_partner?.max_days_past_due_in_90_days
          ?? this.model?.processing?.max_days_past_due_in_90_days
    },
    processingLimitGroup() {
      return this.model?.processing?.odoo_res_partner?.odoo_obligor_limit?.odoo_obligor_limit_group ?? null
    },
    processingLimitSummary() {
      return this.processingCustomer?.limit_summary ?? null
    },
    processingLimitAmount() {
      return Number(this.processingLimitSummary?.limit_amount ?? this.processingLimitGroup?.limit_amount ?? 0)
    },
    processingUsedAmount() {
      return Number(
        this.processingLimitSummary?.used_amount_adjusted
        ?? this.processingLimitGroup?.used_amount
        ?? 0
      )
    },
    processingAvailableAmount() {
      return Number(
        this.processingLimitSummary?.remaining_amount_adjusted
        ?? this.processingLimitGroup?.available_amount
        ?? 0
      )
    },
    processingUsedAmountRatio() {
      if (this.processingLimitAmount <= 0) {
        return 0
      }

      return this.processingUsedAmount / this.processingLimitAmount
    },
    processingUsedAmountProgressPercentage() {
      return Math.max(0, Math.min(100, this.processingUsedAmountRatio * 100))
    },
    limitBadge() {
      const limitAmount = this.processingLimitSummary?.limit_amount ?? this.processingLimitGroup?.limit_amount;
      if (limitAmount === null || limitAmount === undefined) {
        return null;
      }

      const percentage = this.processingUsedAmountRatio * 100;

      if (percentage <= 50) {
        return { label: this.$t('purchase_plans.limit_status.safe'), variant: 'success' };
      } else if (percentage <= 80) {
        return { label: this.$t('purchase_plans.limit_status.warning'), variant: 'warning' };
      } else if (percentage <= 100) {
        return { label: this.$t('purchase_plans.limit_status.danger'), variant: 'danger' };
      } else {
        return { label: this.$t('purchase_plans.limit_status.overlimit'), variant: 'dark' };
      }
    },
    formattedActivityLogs() {
      return this.activity_logs.map(activityLog => {
        return this.formattedPurchasePlanActivityLogs(activityLog);
      }).flat(1) ?? [];
    },
    formattedActivityLogsToShow() {
      return this.formattedActivityLogs.slice(0, this.maxActivityToShowOnCollapse);
    },
    approvalProgressSteps() {
      return collapseApprovalProgressSteps(this.model?.approval_progress?.steps)
    },
    showApproveButton() {
      return this.model?.allowed_actions?.includes('approve')
    },
    showRejectButton() {
      return this.model?.allowed_actions?.includes('reject')
    },
    showEditButton() {
      return this.model?.allowed_actions?.includes('edit') || this.model?.allowed_actions?.includes('update')
    },
    canEditSubmittedAt() {
      return this.model?.allowed_actions?.includes('edit_submitted_at')
    },
    canEditProcessing() {
      return this.model?.allowed_actions?.includes('edit_processing')
    },
    // Past draft the plan is edited in place on this page instead of /edit.
    showInlineEditButton() {
      return this.canEditSubmittedAt || this.canEditProcessing
    },
    isEditingSubmittedAt() {
      return this.isEditing && this.canEditSubmittedAt
    },
    isEditingProcessing() {
      return this.isEditing && this.canEditProcessing
    },
    submittedAtChanged() {
      return this.isEditingSubmittedAt
          && !!this.editingSubmittedAt
          && this.editingSubmittedAt !== this.toDateValue(this.model.submitted_at)
    },
    processingChanged() {
      return this.isEditingProcessing
          && !!this.editingProcessingId
          && Number(this.editingProcessingId) !== Number(this.model.processing_id)
    },
    hasChanges() {
      return this.submittedAtChanged || this.processingChanged
    },
    showDeleteButton() {
      return this.model?.allowed_actions?.includes('delete') || this.model?.allowed_actions?.includes('destroy')
    },
    showCancelButton() {
      return this.model?.allowed_actions?.includes('cancel')
    },
    showDuplicateButton() {
      return this.$auth.hasPermissionTo('purchase_plans.create')
    },
  },
  methods: {
    salesPlanDisplayName,
    currencyFormatter,
    decimalFormatter,
    daysPastDueVariant,
    getModelName(value) {
      return `#${(value || this.model).id || this.$route.params.key}`;
    },
    fetchData() {
      this.loading = true;
      return Promise.all([
        this.fetchModel(),
        this.fetchModelActivityLogs(),
        this.fetchBalancingSaleOrders(),
      ]).then(([response, activityLogRes, balancingSaleOrdersRes]) => {
        const { data: { data } } = response;
        this.model = data;
        const { data: { data: activityLogData } } = activityLogRes;
        this.$set(this, 'activity_logs', activityLogData)
        const { data: { data: balancingSaleOrdersData } } = balancingSaleOrdersRes;
        this.$set(this, 'balancingSaleOrders', balancingSaleOrdersData || [])
        if (this.targetBalancingApprovalId && !this.activeBalancingSaleOrderId) {
          const targetSaleOrder = this.balancingSaleOrders.find((saleOrder) => {
            return Array.isArray(saleOrder.balancing_approval_cycles)
              && saleOrder.balancing_approval_cycles.some((cycle) => `${cycle.id}` === this.targetBalancingApprovalId)
          })
          if (targetSaleOrder) {
            this.activeBalancingSaleOrderId = `${targetSaleOrder.id}`
          }
        }
        if (!this.balancingSwitcherTabs.some((tab) => tab.name === this.activeBalancingSaleOrderId)) {
          this.activeBalancingSaleOrderId = this.balancingSwitcherTabs[0]?.name ?? null
        }
        this.fetchProcessingCustomer(data?.processing?.id)
      })
      .catch((error) => this.handlePurchasePlanLoadError(error, this.$route.params.key))
      .finally(() => (this.loading = false));
    },
    fetchBalancingSaleOrders() {
      return this.$axios
        .get('/sale_orders', {
          params: {
            where_has_purchase_plan_id__in: [this.$route.params.key],
            type__not_in: ["harvest"],
            per_page: 1000,
            with: [
              "balancing_transaction",
              "balancing_approvals.histories.performed_by",
              "balancing_approvals.approval_template.approval_template_steps.approver",
            ].join(","),
          },
        })
        .catch(() => ({ data: { data: [] } }));
    },
    fetchProcessingCustomer(id) {
      if (!id) {
        this.processingCustomer = null;
        return;
      }

      return this.$axios
        .get(`/v1/processing-customers/${id}`)
        .then(({ data }) => {
          this.processingCustomer = (data || {}).data || null;
        })
        .catch(() => {
          this.processingCustomer = null;
        });
    },
    fetchModel() {
      return this.$axios
        .get(`/v1/purchase_plans/${this.$route.params.key}`, {
          params: { with: "product,partner,processing,supervisor,sales_plan.product,approval_template.approval_template_steps.approver,purchase_plan_status_histories.performed_by,creator" },
        });
    },
    fetchModelActivityLogs() {
      return this.$axios.get(`/v1/purchase_plans/${this.$route.params.key}/activity-logs`, {
        params: {
          per_page: 1000,
        },
        timeout: 30000,
      });
    },
    openActivityLogModal() {
      this.$set(this, 'activityLogModalShow', true)
    },
    formattedPurchasePlanActivityLogs(purchasePlanActivityLog) {
      const {description, properties, causer_name} = purchasePlanActivityLog
      let showKeys = [
        'sales_plan_id',
        'product_name',
        'partner_name',
        'processing_name',
        'supervisor_name',
        'payment_type',
        'submitted_at',
        'estimated_size_start',
        'estimated_size_end',
        'estimated_quantity',
        'estimated_amount',
        'estimated_margin',
      ]
      if (description === 'updated') {
        let keys = Object.keys(properties.old)
        return keys.flatMap((key) => {
          if (!showKeys.includes(key)) {
            return []
          }
          const {old, attributes} = properties
          let oldValue = old[key]
          let newValue = attributes[key]

          if (oldValue === newValue) {
            return null
          }

          let needToFormatInCurrencyKeys = ['estimated_amount', 'estimated_margin']
          let needToFormatInDecimalKeys = ['estimated_size_start', 'estimated_size_end', 'estimated_quantity']
          if (needToFormatInCurrencyKeys.includes(key)) {
            oldValue = currencyFormatter(old[key])
            newValue = currencyFormatter(attributes[key])
          }

          if (needToFormatInDecimalKeys.includes(key)) {
            oldValue = decimalFormatter(old[key])
            newValue = decimalFormatter(attributes[key])
          }

          if (key === 'payment_type') {
            oldValue = old[key] ? (this.$te(`purchase_plans.payment_types.${old[key]}`) ? this.$t(`purchase_plans.payment_types.${old[key]}`) : old[key]) : '-'
            newValue = attributes[key] ? (this.$te(`purchase_plans.payment_types.${attributes[key]}`) ? this.$t(`purchase_plans.payment_types.${attributes[key]}`) : attributes[key]) : '-'
          }
          
          if (key === 'submitted_at') {
            oldValue = old[key] ? this.$moment(old[key]).format('DD MMM YYYY') : '-'
            newValue = attributes[key] ? this.$moment(attributes[key]).format('DD MMM YYYY') : '-'
          }

          const labelKey = key === 'processing_name' ? 'processing_destination' : key
          let translatedKey = this.$te(`purchase_plans.${labelKey}`) ? this.$t(`purchase_plans.${labelKey}`) : key

          const createdAt = this.$moment.utc(purchasePlanActivityLog.created_at).local().format('DD-MM-YYYY HH:mm')
          const timestamp = (classes = 'me-2') => `<span class="${classes}" style="color: #667085">${createdAt}</span>`
          const entry = `[${this.lodash.startCase(translatedKey)}] diubah: <b class="text-danger">${oldValue}</b> → <b class="text-success">${newValue}</b> <span style="color:#667085">oleh ${causer_name}</span>`

          const reason = (properties.reasons || {})[key]
          if (!reason) {
            return `${timestamp()}${entry}`
          }

          // The reason is user input and this list is rendered with v-html.
          return `<div class="d-flex">${timestamp('me-2 flex-shrink-0')}<div>${entry}<div style="white-space: pre-line">[${this.lodash.startCase(this.$t('purchase_plans.reason'))}] ${this.lodash.escape(reason)}</div></div></div>`
        }).filter((val => val !== null))
      }

      if (description === 'cancelled') {
        return [`<span class="me-2" style="color: #667085">${this.$moment.utc(purchasePlanActivityLog.created_at).local().format('DD-MM-YYYY HH:mm')}</span>${this.lodash.startCase(this.$t('purchase_plans.singular'))} <b class="text-danger">dibatalkan</b> <span style="color:#667085">oleh ${causer_name}</span>`]
      }

      return [`<span class="me-2" style="color: #667085">${this.$moment.utc(purchasePlanActivityLog.created_at).local().format('DD-MM-YYYY HH:mm')}</span>${this.lodash.startCase(this.$t('purchase_plans.singular'))} <b class="text-success">dibuat</b> <span style="color:#667085">oleh ${causer_name}</span>`]
    },
    balancingStatusDotColor(status) {
      const colors = {
        not_submitted: '#A3A3A3',
        published: '#1664D8',
        in_review: '#DC6803',
        approved: '#15803D',
        rejected: '#DC2626',
      };

      return colors[status] || colors.not_submitted;
    },
    extractProcessingCode(processing) {
      return processing?.code ?? '-'
    },
    extractProcessingName(processing) {
      return processing?.name ?? '-'
    },
    formattedDaysPastDue(daysPastDue) {
      return daysPastDue || '-'
    },
    handleDelete() {
      return this.$refs.deleteConfirmation.show(this.model)
    },
    onDeleted() {
      return this.$router.push('/purchase_plans')
    },
    handleCancel() {
      return this.$refs.cancelConfirmation.show(this.model)
    },
    onCancelled() {
      return this.fetchData()
    },
    handleDuplicate() {
      return this.$refs.duplicateConfirmation.show(this.model)
    },
    approvalStepKey(step, index) {
      const level = step?.level ?? index
      const status = step?.status ?? 'unknown'
      const historyId = step?.history?.id ?? 'no-history'

      return `purchase-plan-approval-step-${level}-${status}-${historyId}`
    },
    approvePurchasePlan(payload = {}) {
      return this.$axios.post(
          `/v1/purchase_plans/${this.$route.params.key}/approve`,
          this.buildActionPayload(payload)
      )
    },
    rejectPurchasePlan(payload = {}) {
      return this.$axios.post(
          `/v1/purchase_plans/${this.$route.params.key}/reject`,
          this.buildActionPayload(payload)
      )
    },
    buildActionPayload({cashflow = null, description = null, reason = null, file = null} = {}) {
      const payload = {}

      if (cashflow !== null && cashflow !== undefined) {
        payload.cashflow = cashflow
      }

      const resolvedText = [description, reason].find((value) => {
        return value !== null && value !== undefined && `${value}`.trim() !== ''
      })

      if (resolvedText !== undefined) {
        const text = `${resolvedText}`.trim()
        payload.description = text
        payload.reason = text
      }

      if (file) {
        payload.file = file
      }

      if (Object.keys(payload).length === 0) {
        return {}
      }

      return serialize(payload, {indices: true, booleansAsIntegers: true})
    },
    showStatusSuccessAlert(messageKey) {
      return Swal.fire({
        title: this.$t('Success') + '!',
        text: this.$t(messageKey, {
          something: this.$route.params.key,
        }),
        type: 'success',
        confirmButtonClass: 'btn btn-success btn-fill',
        buttonsStyling: false
      })
    },
    isAlreadyProcessedError(error) {
      const response = error && error.response
      if (!response) return false

      return response.status === 409
          || (response.data && response.data.code === 'purchase_plan_already_processed')
    },
    handleStatusActionError(error, action) {
      if (this.isAlreadyProcessedError(error)) {
        this.$refs['purchase-plan-action-conflict-dialog'].show(action)
        return this.fetchData()
      }

      return this.failedAlert(error)
    },
    handleApprove() {
      return this.$refs['purchase-plan-approve-confirmation']
          .show({modelId: this.$route.params.key})
          .then((result) => {
            if (!result) return null

            this.$set(this, 'approving', true)
            return this.approvePurchasePlan(result)
                .then(() => this.fetchData())
                .then(() => this.showStatusSuccessAlert('purchase_plans.modal_messages.approve_success_message'))
                .catch((error) => this.handleStatusActionError(error, 'approve'))
                .finally(() => {
                  this.$set(this, 'approving', false)
                })
          })
    },
    handleReject() {
      return this.$refs['purchase-plan-reject-confirmation']
          .show({modelId: this.$route.params.key})
          .then((result) => {
            if (!result || !result.description) return null

            this.$set(this, 'rejecting', true)
            return this.rejectPurchasePlan(result)
                .then(() => this.fetchData())
                .then(() => this.showStatusSuccessAlert('purchase_plans.modal_messages.reject_success_message'))
                .catch((error) => this.handleStatusActionError(error, 'reject'))
                .finally(() => {
                  this.$set(this, 'rejecting', false)
                })
          })
    },
    toDateValue(value) {
      return value ? this.$moment.utc(value).format('YYYY-MM-DD') : null;
    },
    startEditing() {
      this.editingProcessingId = this.model.processing_id;
      this.editingSubmittedAt = this.toDateValue(this.model.submitted_at);
      this.isEditing = true;
    },
    cancelEditing() {
      this.isEditing = false;
      this.editingProcessingId = null;
      this.editingSubmittedAt = null;
    },
    async submitChanges() {
      if (!this.hasChanges || this.saving) {
        return;
      }

      // Only the fields that actually changed are sent; the backend rejects
      // fields that can no longer be edited at the plan's current status.
      const payload = {};

      if (this.processingChanged) {
        payload.processing_id = this.editingProcessingId;
      }

      if (this.submittedAtChanged) {
        const confirmation = await this.$refs.submittedAtConfirmation.show({
          submittedAt: this.$moment(this.editingSubmittedAt).format('DD MMM YYYY'),
        });

        if (!confirmation) {
          return;
        }

        payload.submitted_at = this.editingSubmittedAt;
        payload.reason = confirmation.reason;
      }

      const messageKey = this.submittedAtChanged && this.processingChanged
          ? 'submitted_at_and_processing_updated_message'
          : (this.submittedAtChanged ? 'submitted_at_updated_message' : 'processing_updated_message');

      this.saving = true;

      return this.$axios
        .put(`/v1/purchase_plans/${this.$route.params.key}`, payload)
        .then(() => {
          this.cancelEditing();
          this.$toast(this.$t(`purchase_plans.modal_messages.${messageKey}`), {
            type: "success",
            timeout: 10000,
          });
          return this.fetchData();
        })
        .catch((error) => this.failedAlert(error))
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .preloader-component {
    background-color: #ffffff;
  }
}

.table-container {
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #eaecf0;
  overflow: hidden;
}

.pbarik-progress-section {
  padding-top: 16px;
}

.processing-limit-progress-meta {
  margin-top: 2px;
  font: 400 12px/18px Inter;
  color: #6C7589;
}

.balancing-switcher {
  border-bottom: none;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
}

.balancing-switcher-tab {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
  gap: 6px;
  background-color: transparent;
  border: none;
  border-bottom: 4px solid #E4E7EC;
  border-radius: 0;
  margin-bottom: -1px;
  padding: 6px 12px;
  font: 500 12px/16px Inter;
  color: #6C7589;

  &:hover {
    color: #0083F2;
  }

  &.active {
    color: #0083F2;
    font-weight: 600;
    border-bottom-color: #0083F2;
  }
}

.balancing-switcher-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.approval-history-header {
  margin-bottom: 0;
  font: 600 14px/20px Inter !important;
  color: #6C7589 !important;
}

.container-title {
  margin-top: 10px;
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-align: left;
  margin-bottom: 8px;
  color: #171717;
  text-transform: capitalize;
}

.btn.btn-edit-amber {
  color: #FDB022;
  border-color: #FDB022;
  background-color: transparent;

  &:hover,
  &:focus,
  &:active {
    color: #ffffff;
    border-color: #FDB022;
    background-color: #FDB022;
  }
}

.label {
  font-weight: 700;
  color: #161b4d;
  text-transform: capitalize;
  margin-bottom: 10px;

  &.required::after {
    content: " *";
    color: #DC2626;
  }
}

::v-deep .page-content {
  background: #eef3f7;
}

::v-deep {
  table {
    thead {
      background: #F9FAFB !important;
    }
  }
}

a.activity-log-collapse {
  color: #1664D8;
  font-weight: bold;
}

.change-history-card-text {
  padding: 16px;
  border-radius: 8px;
  background-color: #F8FAFC;
}

.change-history-title {
  font: 600 14px/20px Inter;
}

.activity-log-modal-content {
  max-height: 60vh;
  overflow-y: auto;
}

@import "../../../design/custom/components/status-badge";
</style>

<style lang="scss">
.modal-title.activity-log-modal-title {
  font: 600 16px/20px Inter;
  color: #1664D8 !important;
}
</style>
